import { useEffect, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import type { ArtistDetail, ArtistDetailPageData, ArtistArtworkItem } from '@/types/artist'

/*
  Figma: node 260:766 (Artist Details)
  See in-file comment in the previous revision for layout details.
  Styling lives in src/styles/pages/artist-detail.css (artwork card classes
  are shared with Marketplace.tsx).
*/

interface QA {
  question: string
  answer: string
}

function hasContent(html?: string | null): boolean {
  if (!html) return false
  return html.replace(/<[^>]*>/g, '').trim().length > 0
}

function collectQA(artist: ArtistDetail): QA[] {
  const result: QA[] = []
  for (let i = 1; i <= 6; i++) {
    const q = artist[`question_${i}` as keyof ArtistDetail] as string | null | undefined
    const a = artist[`answer_${i}` as keyof ArtistDetail] as string | null | undefined
    if (hasContent(q) || hasContent(a)) {
      result.push({ question: q ?? '', answer: a ?? '' })
    }
  }
  return result
}

function isVideo(upload?: { extension?: string; url?: string } | null): boolean {
  if (!upload) return false
  const ext = (upload.extension || '').toLowerCase()
  if (!ext && upload.url) {
    const urlExt = upload.url.split('.').pop()?.toLowerCase() || ''
    return ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(urlExt)
  }
  return ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)
}

function splitName(full?: string): { first: string; rest: string } {
  const trimmed = (full ?? '').trim()
  if (!trimmed) return { first: '', rest: '' }
  const idx = trimmed.indexOf(' ')
  if (idx === -1) return { first: trimmed, rest: '' }
  return { first: trimmed.slice(0, idx), rest: trimmed.slice(idx + 1) }
}

/* ---- Social media / portfolio links (IIA fields, June 2026) ----
   Three dedicated URL fields (instagram_link, facebook_link,
   portfolio_link); admins enter plain URLs, so we normalise the
   scheme. Empty fields are skipped; the row hides when all are empty. */
function normalizeUrl(raw?: string | null): string | null {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return null
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

interface ArtistLink {
  label: string
  href: string
}

function collectLinks(artist: ArtistDetail): ArtistLink[] {
  const fields: Array<[string | null | undefined, string]> = [
    [artist.portfolio_link, 'Portfolio'],
    [artist.instagram_link, 'Instagram'],
    [artist.facebook_link, 'Facebook'],
  ]
  const links: ArtistLink[] = []
  for (const [raw, label] of fields) {
    const href = normalizeUrl(raw)
    if (href) links.push({ label, href })
  }
  return links
}

function formatPrice(price: number): string {
  return `$${price.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

/**
 * Artwork card — same visual as the Marketplace grid card (Figma ArtworkItem
 * component), reused here within the artist's artworks section.
 */
function ArtworkCard({ artwork, artistName }: { artwork: ArtistArtworkItem; artistName: string }) {
  const imageUrl =
    artwork.main_image?.versions?.large ||
    artwork.main_image?.versions?.card ||
    artwork.main_image?.url ||
    null

  return (
    <a href={`/marketplace/${artwork.slug}`} className="mig-artwork-card">
      <div className="mig-artwork-card-image">
        {imageUrl ? (
          <img src={imageUrl} alt={artwork.name} loading="lazy" decoding="async" />
        ) : (
          <span className="mig-artwork-card-placeholder">Artwork</span>
        )}
      </div>
      <div className="mig-artwork-card-info">
        <p className="mig-artwork-card-name">{artwork.name}</p>
        <p className="mig-artwork-card-artist">{artistName}</p>
        {artwork.price != null && (
          <p className="mig-artwork-card-price">{formatPrice(artwork.price)}</p>
        )}
      </div>
    </a>
  )
}

export default function ArtistDetailPage() {
  const data = usePageData<ArtistDetailPageData>()
  const artist = data?.artist
  const artworks = data?.artworks ?? []

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (artist?.name) {
      document.title = `${artist.name} | Migration Galleries`
    }
  }, [artist?.name])

  const backHref = (() => {
    try {
      const ref = document.referrer
      if (ref) {
        const url = new URL(ref)
        if (url.pathname.startsWith('/exhibitions/')) return url.pathname
      }
    } catch { /* ignore */ }
    return '/artists'
  })()

  if (!artist) {
    return (
      <section className="mig-artist-detail-not-found">
        <h1 className="mig-artist-detail-page-title">Artist not found</h1>
      </section>
    )
  }

  const { first: nameFirst, rest: nameRest } = splitName(artist.name)
  const studioUrl = artist.studio_shot_or_video?.url || null
  const studioIsVideo = isVideo(artist.studio_shot_or_video)
  const qaList = collectQA(artist)
  const artistLinks = collectLinks(artist)

  const leftCount = Math.ceil(qaList.length / 2)
  const leftColumn = qaList.slice(0, leftCount)
  const rightColumn = qaList.slice(leftCount)

  return (
    <section className="mig-artist-detail-section" data-visible={visible}>
      <div className="mig-artist-detail-content">
        {/* ==== Top row: name + bio (left) | studio media (right) ==== */}
        <div className="mig-artist-detail-hero">
          <div className="mig-artist-detail-hero-left">
            <h1 className="mig-artist-detail-name">
              {nameFirst && <span>{nameFirst}</span>}
              {nameRest && <span>{nameRest}</span>}
            </h1>

            {/* Portfolio + social links (IIA-managed), directly below the
                name. Hidden entirely when no link field is populated. */}
            {artistLinks.length > 0 && (
              <div className="mig-artist-detail-links">
                {artistLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mig-artist-detail-link"
                  >
                    {link.label} &gt;
                  </a>
                ))}
              </div>
            )}

            {hasContent(artist.bio) && (
              <div
                className="mig-artist-detail-bio"
                dangerouslySetInnerHTML={{ __html: artist.bio ?? '' }}
              />
            )}
          </div>

          <div className="mig-artist-detail-studio">
            {studioUrl ? (
              studioIsVideo ? (
                <video src={studioUrl} controls playsInline />
              ) : (
                <img src={studioUrl} alt={`${artist.name} studio`} decoding="async" />
              )
            ) : (
              <span className="mig-artist-detail-studio-placeholder">Studio shot or video</span>
            )}
          </div>
        </div>

        {/* ==== Artworks section ==== */}
        {artworks.length > 0 && (
          <>
            <div className="mig-divider-thick-12" />

            <div className="mig-artist-artworks-container">
              {(() => {
                const rows: ArtistArtworkItem[][] = []
                for (let i = 0; i < artworks.length; i += 4) {
                  rows.push(artworks.slice(i, i + 4))
                }
                return rows.map((row, rowIndex) => (
                  <div key={rowIndex} className="mig-artist-artworks-row">
                    {row.map((aw) => (
                      <ArtworkCard key={aw.id} artwork={aw} artistName={artist.name ?? ''} />
                    ))}
                    {row.length < 4 &&
                      Array.from({ length: 4 - row.length }).map((_, i) => (
                        <div key={`pad-${i}`} className="mig-artist-artworks-row-spacer" aria-hidden="true" />
                      ))}
                  </div>
                ))
              })()}
            </div>
          </>
        )}

        {/* ==== Q&A section ==== */}
        {qaList.length > 0 && (
          <div className="mig-artist-detail-qa-section">
            <div className="mig-divider-thick-12" />

            <div className="mig-artist-detail-qa-header">
              <p className="mig-artist-detail-qa-title">Q&amp;A</p>
              <div className="mig-artist-detail-qa-strapline">
                <p>A set of identical</p>
                <p>questions, answered</p>
                <p>differently by every artist</p>
              </div>
            </div>

            <div className="mig-divider-line" />

            <div className="mig-artist-detail-qa-grid">
              <div className="mig-artist-detail-qa-col">
                {leftColumn.map((qa, i) => (
                  <div key={`l-${i}`} className="mig-artist-detail-qa-item">
                    {hasContent(qa.question) && (
                      <div
                        className="mig-artist-detail-qa-question"
                        dangerouslySetInnerHTML={{ __html: qa.question }}
                      />
                    )}
                    {hasContent(qa.answer) && (
                      <div
                        className="mig-artist-detail-qa-answer"
                        dangerouslySetInnerHTML={{ __html: qa.answer }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {rightColumn.length > 0 && (
                <div className="mig-artist-detail-qa-col">
                  {rightColumn.map((qa, i) => (
                    <div key={`r-${i}`} className="mig-artist-detail-qa-item">
                      {hasContent(qa.question) && (
                        <div
                          className="mig-artist-detail-qa-question"
                          dangerouslySetInnerHTML={{ __html: qa.question }}
                        />
                      )}
                      {hasContent(qa.answer) && (
                        <div
                          className="mig-artist-detail-qa-answer"
                          dangerouslySetInnerHTML={{ __html: qa.answer }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mig-divider-thick-12" />
          </div>
        )}

        {/* BACK button */}
        <div className="mig-page-back-row">
          <a href={backHref} className="mig-artist-detail-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </div>
    </section>
  )
}
